import { ChangeDetectionStrategy, Component, Type, input } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideInbox } from '@lucide/angular';

/**
 * Friendly "nothing here" message with an optional next step:
 *   <ds-empty-state title="…" description="…"><ds-button>…</ds-button></ds-empty-state>
 */
@Component({
  selector: 'ds-empty-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    <div class="empty">
      <span
        class="empty__icon"
        aria-hidden="true"
        [style.background]="iconBg()"
        [style.color]="iconColor()"
      >
        <ng-container
          [ngComponentOutlet]="icon()"
          [ngComponentOutletInputs]="{ size: 44, strokeWidth: 1.5 }"
        />
      </span>
      <h3 class="empty__title">{{ title() }}</h3>
      @if (description()) {
        <p class="empty__description">{{ description() }}</p>
      }
      <div class="empty__action">
        <ng-content />
      </div>
    </div>
  `,
  styles: `
    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--space-7) var(--space-5);
      text-align: center;
    }

    .empty__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 88px;
      height: 88px;
      margin-bottom: var(--space-4);
      border-radius: 50%;
    }

    .empty__title {
      margin: 0 0 var(--space-2);
      font-size: var(--text-xl);
    }

    .empty__description {
      max-width: 44ch;
      margin: 0;
      color: var(--color-text-secondary);
    }

    .empty__action {
      margin-top: var(--space-5);

      &:empty {
        display: none;
      }
    }
  `,
})
export class EmptyStateComponent {
  title = input.required<string>();
  description = input<string | undefined>(undefined);
  icon = input<Type<unknown>>(LucideInbox);
  iconColor = input<string>('var(--color-primary)');
  iconBg = input<string>('var(--color-primary-tint)');
}
